import React, { useState, useEffect, useRef } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { API_ENDPOINT } from "../../../../../constants/constants";

export default function CitySelector({
  countryCode,
  stateCode,
  selectedCity,
  onChange,
  required,
}) {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      if (!countryCode || !stateCode) {
        setCities([]);
        return;
      }

      // Cancel previous request
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        // if (mountedRef.current) setLoading(true);

        const response = await fetch(
          `${API_ENDPOINT}visitor/get-cities/${countryCode}/${stateCode}`,
          { signal: controller.signal }
        );
        const data = await response.json();

        if (!controller.signal.aborted && mountedRef.current) {
          const cityData = data.map((city) => ({ name: city.name }));
          setCities(cityData);
        }
      } catch (error) {
        if (error.name !== "AbortError" && mountedRef.current) {
          console.error("Error fetching cities:", error);
          setCities([]);
        }
      } finally {
        if (!controller.signal.aborted && mountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchCities();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [countryCode, stateCode]);

  const handleChange = (event, value) => {
    onChange?.(value);
  };

  return (
    <Autocomplete
      size="small"
      options={cities}
      value={selectedCity || null}
      onChange={handleChange}
      getOptionLabel={(option) => (option ? option.name : "")}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      loading={loading}
      disabled={!countryCode || !stateCode || loading}
      noOptionsText={
        !countryCode
          ? "Select country first"
          : !stateCode
          ? "Select state first"
          : "No cities available"
      }
      renderInput={(params) => (
        <TextField
          required={required}
          {...params}
          label="Select City"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
